import * as Tooltip from '@radix-ui/react-tooltip'
import { Plus, RotateCcw, X } from 'lucide-react'
import { REVEAL_ON_HOVER } from '@/components/ui/reveal-on-hover'
import { cn } from '@/lib/utils'
import type { TestCaseTabsProps } from './types'

export function TestCaseTabs({
  cases,
  activeCaseId,
  onSelect,
  onCloseCase,
  onAddCase,
  onResetCases,
  showResetButton,
  disableAddCase,
}: TestCaseTabsProps) {
  return (
    <header className="border-border/60 border-b px-4 py-2">
      <nav
        className="flex items-center gap-2"
        aria-label="Test case navigation"
      >
        <div className="flex-1">
          <div
            role="tablist"
            aria-label="Test cases"
            className="flex flex-wrap items-center gap-2 pr-2"
          >
            {cases.map((testCase, index) => {
              const isActive = activeCaseId === testCase._id
              return (
                <div
                  key={testCase._id}
                  role="tab"
                  tabIndex={0}
                  aria-selected={isActive}
                  onClick={() => onSelect(testCase._id)}
                  onKeyDown={(event) => {
                    if (event.key === 'Enter' || event.key === ' ') {
                      event.preventDefault()
                      onSelect(testCase._id)
                    }
                  }}
                  className={`group focus-visible:ring-ring relative inline-flex cursor-pointer items-center gap-1.5 rounded-md border px-3 py-1.5 text-sm font-medium transition-colors focus-visible:ring-2 focus-visible:outline-none ${
                    isActive
                      ? 'border-border text-foreground bg-black/5 dark:bg-white/10'
                      : 'text-muted-foreground hover:text-foreground border-transparent hover:bg-black/5 dark:hover:bg-white/10'
                  }`}
                >
                  <span className="select-none">Case {index + 1}</span>
                  <button
                    type="button"
                    aria-label={`Close Case ${index + 1}`}
                    onClick={(event) => {
                      event.stopPropagation()
                      onCloseCase(testCase._id)
                    }}
                    className={cn(
                      'border-border bg-background text-muted-foreground hover:bg-foreground/10 hover:text-foreground focus-visible:ring-ring dark:bg-muted absolute -top-1.5 -right-1.5 inline-flex h-4 w-4 items-center justify-center rounded-full border shadow-sm focus-visible:ring-2 focus-visible:outline-none',
                      REVEAL_ON_HOVER,
                    )}
                  >
                    <X className="h-2.5 w-2.5" />
                  </button>
                </div>
              )
            })}
          </div>
        </div>
        <div className="flex items-center gap-2">
          {showResetButton ? (
            <button
              type="button"
              onClick={onResetCases}
              className="border-border text-muted-foreground hover:border-primary hover:text-foreground focus-visible:ring-ring inline-flex items-center gap-1 rounded-md border px-2 py-1.5 text-xs font-medium transition focus-visible:ring-2 focus-visible:outline-none"
            >
              <RotateCcw className="h-4 w-4" />
              Reset
            </button>
          ) : null}
          <Tooltip.Provider delayDuration={150}>
            <Tooltip.Root>
              <Tooltip.Trigger asChild>
                <button
                  type="button"
                  onClick={onAddCase}
                  disabled={disableAddCase}
                  className="border-border text-muted-foreground hover:border-primary hover:text-foreground focus-visible:ring-ring inline-flex items-center gap-1 rounded-md border border-dashed px-2 py-1.5 text-xs font-medium transition focus-visible:ring-2 focus-visible:outline-none disabled:cursor-not-allowed disabled:opacity-50"
                >
                  <Plus className="h-4 w-4" />
                  Add Case
                </button>
              </Tooltip.Trigger>
              <Tooltip.Content
                side="top"
                align="center"
                className="z-[100] border-border bg-background text-foreground rounded-md border px-3 py-1.5 text-sm font-medium shadow-md"
              >
                Add new empty test case
                <Tooltip.Arrow className="fill-background" />
              </Tooltip.Content>
            </Tooltip.Root>
          </Tooltip.Provider>
        </div>
      </nav>
    </header>
  )
}
